import * as React from 'react';
import { styled } from '@linaria/react';
import {
  Info,
  AlertTriangle,
  AlertOctagon,
  CheckCircle,
} from 'lucide-react';
import { useSpring, animated } from 'react-spring';

import { LIGHT_COLORS_RAW } from '@/constants';
import usePrefersReducedMotion from '@/hooks/use-prefers-reduced-motion';
import useRequestAnimationFrameLoop from '@/hooks/use-request-animation-frame-loop';

import IconDismiss from '@/components/Icons/IconDismiss';
import { LightWrapper } from '@/components/ColorModeContainer';
import VisuallyHidden from '@/components/VisuallyHidden';
import InPortal from '@/components/InPortal';

const ICONS_BY_TYPE = {
  info: Info,
  success: CheckCircle,
  warning: AlertTriangle,
  error: AlertOctagon,
};

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  id?: string;
  type: keyof typeof ICONS_BY_TYPE;
  title: string;
  handleDismiss: () => void;
  popTimeout?: number;
  children: React.ReactNode;
}

function Toast({
  id,
  type,
  title,
  handleDismiss,
  popTimeout = 400,
  children,
  ...delegated
}: Props) {
  const Icon = ICONS_BY_TYPE[type];

  const prefersReducedMotion = usePrefersReducedMotion();

  const [poppedAt, setPoppedAt] = React.useState<false | number>(
    false
  );
  const [hasBeenInterrupted, setHasBeenInterrupted] =
    React.useState(false);

  const [dismissBtnStatus, setDismissBtnStatus] = React.useState<
    'idle' | 'booped' | 'pressed'
  >('idle');

  // `totalTime` will be the number of milliseconds to show the toast for, *or* it will be `null` if the toast should not auto-dismiss.
  const totalTime = 30_000;
  const [timeRemaining, setTimeRemaining] = React.useState(totalTime);

  React.useEffect(() => {
    const timeoutId = window.setTimeout(
      () => setPoppedAt(Date.now()),
      popTimeout
    );

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [popTimeout]);

  React.useEffect(() => {
    if (dismissBtnStatus !== 'booped') {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setDismissBtnStatus('idle');
    }, 150);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [dismissBtnStatus]);

  React.useEffect(() => {
    if (!poppedAt) {
      return;
    }

    function handleKeyDown(ev: KeyboardEvent) {
      if (ev.key === 'Escape') {
        setPoppedAt(false);
      }
    }

    window.addEventListener('keydown', handleKeyDown, {
      capture: true,
    });

    return () => {
      window.removeEventListener('keydown', handleKeyDown, {
        capture: true,
      });
    };
  }, [poppedAt]);

  const isCountingDown =
    typeof poppedAt === 'number' &&
    typeof timeRemaining === 'number' &&
    timeRemaining > 0 &&
    !hasBeenInterrupted;

  useRequestAnimationFrameLoop(() => {
    if (typeof poppedAt !== 'number') {
      return;
    }
    const timeElapsed = Date.now() - poppedAt;
    const nextTimeRemaining = Math.max(0, totalTime - timeElapsed);

    if (nextTimeRemaining === 0) {
      // We don't want to auto-dismiss *right* away, we want to do it after an additional delay.
      window.setTimeout(() => setPoppedAt(false), 500);
    }

    setTimeRemaining(nextTimeRemaining);
  }, isCountingDown);

  const style = useSpring({
    from: { transform: 'translateY(150%)' },
    to: {
      transform: poppedAt ? 'translateY(0px)' : 'translateY(150%)',
    },
    config: {
      tension: 350,
      friction: 20,
      mass: 2,
      precision: 0.001,
    },
    immediate: prefersReducedMotion,
  });

  const hasPoppedAlready = React.useRef(false);
  const handleDismissRef = React.useRef<any>();
  handleDismissRef.current = handleDismiss;

  React.useEffect(() => {
    let timeoutId: number;
    if (poppedAt) {
      hasPoppedAlready.current = true;
    } else if (hasPoppedAlready.current) {
      timeoutId = window.setTimeout(handleDismissRef.current, 500);
    }

    return () => window.clearTimeout(timeoutId);
  }, [poppedAt]);

  const barScale =
    typeof timeRemaining === 'number' && !hasBeenInterrupted
      ? timeRemaining / totalTime
      : 1;

  // success -> colorSuccess500
  const colorName = `color${type[0].toUpperCase()}${type.slice(1)}500`;

  return (
    <InPortal>
      <OuterWrapper
        {...delegated}
        onMouseEnter={(ev) => {
          setHasBeenInterrupted(true);
          if (typeof delegated.onMouseEnter === 'function') {
            delegated.onMouseEnter(ev);
          }
        }}
        onFocus={(ev) => {
          setHasBeenInterrupted(true);
          if (typeof delegated.onFocus === 'function') {
            delegated.onFocus(ev);
          }
        }}
        data-test="toast"
        data-test-type={type}
        data-test-title={title}
        data-test-body={children}
        data-toast-id={id}
      >
        <Wrapper
          as={animated.div}
          // More issues with `as`
          // @ts-ignore
          style={{
            '--h': LIGHT_COLORS_RAW[colorName][0] + 'deg',
            '--s': LIGHT_COLORS_RAW[colorName][1] + '%',
            '--l': LIGHT_COLORS_RAW[colorName][2] + '%',
            ...style,
          }}
        >
          <TimeColumn>
            <TimeRemaining
              style={{
                '--transform': `scaleY(${barScale})`,
                // While counting down, we update super frequently inside a requestAnimationFrame loop, and so no `transition` is required. If we're NOT counting down, though, we want to add a CSS transition. This causes the bar to grow back to full height when the countdown is interrupted (eg. with a hover).
                '--transition': isCountingDown
                  ? 'none'
                  : 'transform 400ms',
                '--gradient-opacity': isCountingDown ? 1 : 0,
              }}
            />
          </TimeColumn>
          <Icon
            size={24}
            style={{ color: `hsl(var(--h) var(--s) var(--l))` }}
          />
          <Main>
            <Heading>{title}</Heading>
            {typeof children === 'string' ? (
              <p>{children}</p>
            ) : (
              children
            )}
          </Main>
          <CloseButton
            onClick={() => setPoppedAt(false)}
            onMouseEnter={() => setDismissBtnStatus('booped')}
            onMouseLeave={() => setDismissBtnStatus('idle')}
            onMouseDown={() => setDismissBtnStatus('pressed')}
            onMouseUp={() => setDismissBtnStatus('idle')}
          >
            <IconDismiss
              size={24}
              isPressed={dismissBtnStatus === 'pressed'}
              isBooped={dismissBtnStatus === 'booped'}
            />
            <VisuallyHidden>Dismiss Toast</VisuallyHidden>
          </CloseButton>
        </Wrapper>
      </OuterWrapper>
    </InPortal>
  );
}

const OuterWrapper = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  padding: 16px;
  display: flex;
  justify-content: center;

  @media print {
    display: none;
  }
`;

const CloseButton = styled.button`
  color: var(--color-gray-700);

  &:hover {
    color: var(--color-text);
  }
`;

const Wrapper = styled(LightWrapper)`
  display: flex;
  align-items: center;
  gap: 16px;
  padding-right: 16px;
  box-shadow:
    0px 2px 4px hsl(0deg 0% 0% / 0.1),
    0px 4px 8px hsl(0deg 0% 0% / 0.1),
    0px 8px 16px hsl(0deg 0% 0% / 0.1),
    0px 16px 32px hsl(0deg 0% 0% / 0.1);
  border-radius: 4px;
  max-width: 600px;
  pointer-events: auto;
  will-change: transform;
  /* Trim corners */
  overflow: hidden;
  overflow: clip;

  svg {
    display: block !important;
  }
`;

const TimeColumn = styled.div`
  align-self: stretch;
  position: relative;
  width: 6px;
  background: hsl(
    var(--h) calc(var(--s) * 0.675) calc(var(--l) * 0.875)
  );

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    height: 100%;
    width: 100%;
    background: linear-gradient(
      to right,
      transparent 50%,
      hsl(var(--h) calc(var(--s) * 0.675) calc(var(--l) * 0.75))
    );
  }
`;

const TimeRemaining = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  inset: 0;
  border-radius: 2px 2px 0 0;
  transform-origin: center bottom;
  background: hsl(from var(--main-color) h s calc(l * 1.1));
  background: hsl(var(--h) var(--s) calc(var(--l) * 1.1));

  /* Trim corners of ::after */
  overflow: hidden;
  overflow: clip;
  will-change: transform;

  @media (prefers-reduced-motion: no-preference) {
    transform: var(--transform);
    transition: var(--transition);
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 50%;
    background: linear-gradient(
      to bottom,
      hsl(0deg 0% 100% / 0.333),
      hsl(0deg 0% 100% / 0)
    );

    @media (prefers-reduced-motion: no-preference) {
      opacity: var(--gradient-opacity);
      transition: opacity 300ms;
    }
  }
`;

const Main = styled.div`
  flex: 1;
  padding-top: 12px;
  padding-bottom: 15px;

  p {
    margin-bottom: 0.5em;
    font-size: 1rem;
    color: var(--color-gray-800);
  }

  p:last-of-type {
    margin-bottom: 0;
  }
`;

const Heading = styled.h2`
  font-weight: var(--font-weight-medium);
  font-size: 1rem;
  margin-bottom: 4px;
`;

export default Toast;
